import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { ChartConfiguration } from 'chart.js';
import { finalize } from 'rxjs';
import { AlarmSummary, DeviceTelemetryResponse, LatestTelemetryDevice, TelemetrySeries } from '../models/dashboard.model';
import { DashboardService } from '../services/dashboard.service';
import { buildDeviceStatus, decodeStateFlags } from '../utils/telemetry-status';
import { formatCompanyDateTime, formatCompanyTime, parsePlatformTimestamp } from '../utils/company-date';

interface EvidenceRow {
  time: string;
  status: string;
  flags: string[];
  deviceState: number | string | null;
  temperature: number | string | null;
  humidity: number | string | null;
  battery: number | string | null;
}

type TimelineStatus = 'active' | 'alarm' | 'hibernating';

interface StatusSegment {
  status: TimelineStatus;
  start: string;
  end: string;
  left: number;
  width: number;
}

@Component({
  selector: 'app-device-operations',
  templateUrl: './device-operations.component.html',
  styleUrl: './device-operations.component.css',
})
export class DeviceOperationsComponent implements OnInit, OnChanges {
  @Input({ required: true }) companyId = 0;
  @Input({ required: true }) deviceId = 0;
  @Input() mode: 'telemetry' | 'alarms' = 'telemetry';
  @Input() focusAt?: string;
  @Input() latestTelemetry: LatestTelemetryDevice | null = null;

  rangeHours = 24;
  telemetry: DeviceTelemetryResponse | null = null;
  evidenceRows: EvidenceRow[] = [];
  evidenceRowsNewestFirst: EvidenceRow[] = [];
  statusEvents: Array<{ time: string; status: string; flags: string[] }> = [];
  statusSegments: StatusSegment[] = [];
  statusTimelineTicks: Array<{ label: string; offset: number }> = [];
  alarms: AlarmSummary[] = [];
  nextCursor: string | null = null;
  loading = false;
  error = false;
  acknowledging = new Set<string>();
  acknowledgementErrors = new Set<string>();
  showStatusTimeline = true;
  showTelemetrySummary = true;
  showEvidence = false;
  readonly statusTimelineStates: TimelineStatus[] = ['active', 'alarm', 'hibernating'];
  private telemetryRangeStart = 0;
  private telemetryRangeEnd = 0;

  readonly ranges = [
    { label: '1h', hours: 1 },
    { label: '6h', hours: 6 },
    { label: '24h', hours: 24 },
    { label: '7d', hours: 168 },
  ];

  chartData: ChartConfiguration<'line'>['data'] = { labels: [], datasets: [] };
  readonly chartOptions: ChartConfiguration<'line'>['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { intersect: false, mode: 'index' },
    scales: {
      x: { grid: { display: false }, ticks: { maxTicksLimit: 8, color: '#687883' } },
      y: { grid: { color: '#e8edf0' }, ticks: { color: '#687883' } },
    },
    plugins: { legend: { position: 'bottom', labels: { usePointStyle: true, pointStyle: 'line', boxWidth: 18, boxHeight: 8, padding: 22, font: { family: 'Inter, Arial, sans-serif', size: 12 } } } },
  };

  constructor(private dashboardService: DashboardService) {}

  ngOnInit(): void {
    this.load();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const shouldReload = ['companyId', 'deviceId', 'mode', 'focusAt']
      .some(name => changes[name] && !changes[name].firstChange);
    if (shouldReload) this.load();
    else if (changes['latestTelemetry'] && this.telemetry) {
      this.buildEvidence();
      this.buildChart();
    }
  }

  get currentStatus(): string {
    const deviceState = this.latestPoint('device_state');
    const state = this.latestPoint('state');
    if (!deviceState && !state) return 'no-data';
    return buildDeviceStatus({
      deviceId: this.deviceId,
      measures: {
        ...(deviceState ? { device_state: this.toMeasure(deviceState, 'device_state') } : {}),
        ...(state ? { state: this.toMeasure(state, 'state') } : {}),
      },
    }).key;
  }

  get stateFlags(): string[] {
    return decodeStateFlags(this.latestPoint('state')?.value);
  }

  load(): void {
    if (!this.companyId || !this.deviceId || this.loading) return;
    this.mode === 'telemetry' ? this.loadTelemetry() : this.loadAlarms(false);
  }

  selectRange(hours: number): void {
    if (this.rangeHours === hours) return;
    this.rangeHours = hours;
    this.loadTelemetry();
  }

  toggleStatusTimeline(): void {
    this.showStatusTimeline = !this.showStatusTimeline;
  }

  toggleTelemetrySummary(): void {
    this.showTelemetrySummary = !this.showTelemetrySummary;
  }

  toggleEvidence(): void {
    this.showEvidence = !this.showEvidence;
  }

  loadTelemetry(): void {
    this.loading = true;
    this.error = false;
    this.telemetry = null;
    this.evidenceRows = [];
    this.evidenceRowsNewestFirst = [];
    this.statusEvents = [];
    this.statusSegments = [];
    this.statusTimelineTicks = [];
    const focusedAt = this.focusAt ? parsePlatformTimestamp(this.focusAt) : Number.NaN;
    const to = Number.isNaN(focusedAt)
      ? new Date()
      : new Date(focusedAt + (this.rangeHours * 30 * 60 * 1000));
    const from = new Date(to.getTime() - this.rangeHours * 60 * 60 * 1000);
    this.telemetryRangeStart = from.getTime();
    this.telemetryRangeEnd = to.getTime();
    this.dashboardService.getDeviceTelemetry(this.companyId, this.deviceId, from, to).pipe(
      finalize(() => this.loading = false),
    ).subscribe({
      next: telemetry => {
        this.telemetry = telemetry;
        this.buildEvidence();
        this.buildChart();
      },
      error: () => this.error = true,
    });
  }

  loadAlarms(append: boolean): void {
    this.loading = true;
    this.error = false;
    this.dashboardService.getAlarms(this.companyId, {
      deviceId: this.deviceId,
      limit: 50,
      ...(append && this.nextCursor ? { cursor: this.nextCursor } : {}),
    }).pipe(finalize(() => this.loading = false)).subscribe({
      next: page => {
        this.alarms = append ? [...this.alarms, ...page.alarms] : page.alarms;
        this.nextCursor = page.nextCursor;
      },
      error: () => this.error = true,
    });
  }

  acknowledge(alarm: AlarmSummary): void {
    if (this.acknowledging.has(alarm.alarmId)) return;
    this.acknowledgementErrors.delete(alarm.alarmId);
    this.acknowledging.add(alarm.alarmId);
    this.dashboardService.acknowledgeAlarm(this.companyId, alarm.alarmId).pipe(
      finalize(() => this.acknowledging.delete(alarm.alarmId)),
    ).subscribe({
      next: updated => this.alarms = this.alarms.map(item => item.alarmId === alarm.alarmId ? updated : item),
      error: () => this.acknowledgementErrors.add(alarm.alarmId),
    });
  }

  latestValue(measure: string): number | string | null {
    return this.latestPoint(measure)?.value ?? null;
  }

  hasMetricData(): boolean {
    return this.chartData.datasets.some(dataset => dataset.data.length > 0);
  }

  alarmTypeKey(alarm: AlarmSummary): string {
    return alarm.alarmType?.trim() || 'generic';
  }

  private series(measure: string): TelemetrySeries | undefined {
    return this.telemetry?.series.find(series => series.measure === measure);
  }

  private latestPoint(measure: string) {
    const points = this.series(measure)?.points ?? [];
    return points.length ? points[points.length - 1] : null;
  }

  private toMeasure(point: { time: string; value: number | string | null }, schema: string) {
    return { time: point.time, value: point.value, valueType: 'number' as const, encoding: 'bitmask', bitmaskSchema: schema };
  }

  private buildEvidence(): void {
    const values = new Map<string, Map<string, number | string | null>>();
    ['device_state', 'state', 'temperature', 'humidity', 'battery'].forEach(measure => {
      values.set(measure, new Map((this.series(measure)?.points ?? []).map(point => [point.time, point.value])));
    });
    Object.entries(this.latestTelemetry?.measures ?? {}).forEach(([measure, point]) => {
      if (point.time && values.has(measure)) values.get(measure)!.set(point.time, point.value);
    });
    const times = [...new Set([...values.values()].flatMap(series => [...series.keys()]))].sort();
    this.evidenceRows = times.map(time => {
      const deviceState = values.get('device_state')?.get(time) ?? null;
      const state = values.get('state')?.get(time) ?? null;
      const status = buildDeviceStatus({
        deviceId: this.deviceId,
        measures: {
          ...(deviceState !== null ? { device_state: this.toMeasure({ time, value: deviceState }, 'device_state') } : {}),
          ...(state !== null ? { state: this.toMeasure({ time, value: state }, 'state') } : {}),
        },
      });
      return {
        time,
        status: status.key,
        flags: decodeStateFlags(state),
        deviceState,
        temperature: values.get('temperature')?.get(time) ?? null,
        humidity: values.get('humidity')?.get(time) ?? null,
        battery: values.get('battery')?.get(time) ?? null,
      };
    });
    this.evidenceRowsNewestFirst = this.evidenceRows.filter(row => row.flags.length > 0).reverse();
    this.statusEvents = this.evidenceRows
      .filter(row => row.deviceState !== null)
      .map(row => ({ time: row.time, status: row.status, flags: row.flags }));
    this.buildStatusTimeline();
  }

  private buildStatusTimeline(): void {
    const events = this.statusEvents
      .filter((event): event is { time: string; status: TimelineStatus; flags: string[] } => this.statusTimelineStates.includes(event.status as TimelineStatus))
      .sort((left, right) => parsePlatformTimestamp(left.time) - parsePlatformTimestamp(right.time));
    const from = this.telemetryRangeStart || parsePlatformTimestamp(events[0]?.time);
    const lastEvent = parsePlatformTimestamp(events[events.length - 1]?.time);
    const to = Math.max(this.telemetryRangeEnd, lastEvent);

    if (!events.length || !Number.isFinite(from) || !Number.isFinite(to) || to <= from) return;

    const slices = events.map((event, index) => ({
      status: event.status,
      start: Math.max(parsePlatformTimestamp(event.time), from),
      end: Math.min(index < events.length - 1 ? parsePlatformTimestamp(events[index + 1].time) : to, to),
    })).filter(segment => segment.end > segment.start);
    const merged = slices.reduce<Array<{ status: TimelineStatus; start: number; end: number }>>((segments, segment) => {
      const previous = segments[segments.length - 1];
      if (previous && previous.status === segment.status && previous.end === segment.start) previous.end = segment.end;
      else segments.push(segment);
      return segments;
    }, []);
    const duration = to - from;
    this.statusSegments = merged.map(segment => ({
      status: segment.status,
      start: new Date(segment.start).toISOString(),
      end: new Date(segment.end).toISOString(),
      left: ((segment.start - from) / duration) * 100,
      width: ((segment.end - segment.start) / duration) * 100,
    }));
    this.statusTimelineTicks = Array.from({ length: 5 }, (_, index) => {
      const offset = (index / 4) * 100;
      return { offset, label: formatCompanyTime(from + ((to - from) * index / 4)) };
    });
  }

  private buildChart(): void {
    const colors: Record<string, string> = { temperature: '#c65d28', battery: '#2c7a55', humidity: '#3178a8' };
    const labels = new Set<string>();
    ['temperature', 'battery', 'humidity'].forEach(measure => {
      (this.series(measure)?.points ?? []).forEach(point => labels.add(point.time));
    });
    const orderedLabels = [...labels].sort();
    this.chartData = {
      labels: orderedLabels.map(formatCompanyDateTime),
      datasets: ['temperature', 'battery', 'humidity'].map(measure => {
        const values = new Map((this.series(measure)?.points ?? []).map(point => [point.time, Number(point.value)]));
        return {
          label: measure,
          data: orderedLabels.map(time => values.has(time) && Number.isFinite(values.get(time)) ? values.get(time)! : null),
          borderColor: colors[measure],
          backgroundColor: `${colors[measure]}1f`,
          pointRadius: 0,
          pointHoverRadius: 4,
          borderWidth: 2,
          spanGaps: true,
          tension: 0.2,
        };
      }),
    };
  }
}
